import React, { useEffect, useRef } from 'react';
import { Bold, Italic, Link2, List, ListOrdered, Underline } from 'lucide-react';

// Ticket 195: the email composer's formatting box. A contentEditable div with
// a small toolbar; what it produces is only a draft — the server cuts it down
// to an allowlist (backend/integrations/ghl/send.py) before anything is sent.
// Uncontrolled: `initialHtml` is read when the editor mounts (remount it with a
// new `key` to reset), and every edit reports `onChange(html, text)`.
const SAFE_LINK = /^(https?:\/\/|mailto:)\S+$/i;

const TOOLS = [
  ['bold', 'Bold', Bold],
  ['italic', 'Italic', Italic],
  ['underline', 'Underline', Underline],
  ['insertUnorderedList', 'Bulleted list', List],
  ['insertOrderedList', 'Numbered list', ListOrdered],
];

export function escapeHtml(text) {
  return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// A signature (plain text) as the editor's starting content: an empty line to
// type in, then one paragraph per signature line.
export function signatureHtml(signature) {
  const lines = String(signature || '').split('\n').map((l) => l.trim()).filter(Boolean);
  return lines.length ? `<p><br></p>${lines.map((l) => `<p>${escapeHtml(l)}</p>`).join('')}` : '';
}

const exec = (cmd, value) => document.execCommand?.(cmd, false, value);

export default function RichEmailEditor({ initialHtml = '', onChange, disabled = false }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    el.innerHTML = initialHtml;
    // Enter makes a <p>, not a <div>, so the server sees paragraphs.
    exec('defaultParagraphSeparator', 'p');
    // Read on mount only; a new `key` remounts to reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const report = () => {
    const el = ref.current;
    onChange?.(el.innerHTML, (el.innerText ?? el.textContent) || '');
  };

  const run = (cmd, value) => {
    ref.current.focus();
    exec(cmd, value);
    report();
  };

  const addLink = () => {
    const url = (window.prompt('Link address (https://…)') || '').trim();
    if (url && SAFE_LINK.test(url)) run('createLink', url);
  };

  return (
    <div className={`flex-1 min-w-0 border border-input rounded-md bg-white ${disabled ? 'opacity-60' : ''}`} data-testid="rich-editor">
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-slate-100" role="toolbar" aria-label="Formatting">
        {TOOLS.map(([cmd, label, Icon]) => (
          <button key={cmd} type="button" title={label} aria-label={label} data-testid={`fmt-${cmd}`}
            onMouseDown={(e) => e.preventDefault()} onClick={() => run(cmd)}
            className="p-1 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800">
            <Icon size={14} />
          </button>
        ))}
        <button type="button" title="Link" aria-label="Link" data-testid="fmt-link"
          onMouseDown={(e) => e.preventDefault()} onClick={addLink}
          className="p-1 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800">
          <Link2 size={14} />
        </button>
      </div>
      <div ref={ref} contentEditable={!disabled} suppressContentEditableWarning role="textbox" aria-multiline="true"
        aria-label="Message" onInput={report} data-testid="message-draft"
        className="px-3 py-2 text-sm min-h-[88px] max-h-56 overflow-y-auto focus:outline-none [&_a]:text-blue-600 [&_a]:underline [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:ml-5 [&_ol]:ml-5" />
    </div>
  );
}
